import { css } from '@emotion/react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { useDevToolsStore } from '@frontend/devtools';
import { ScheduleRequestListItem } from '@frontend/schedule-helpers';
import { Accordion, ContentLoader, useModalControl } from '@frontend/design-system';
import { useCheckPaymentsEnabled } from '../../../../../../../hooks/use-check-payments-enabled';
import { useOpenedRequestDetails } from '../../hooks/use-opened-request-details';
import { useScheduleRequestActions } from '../../hooks/use-schedule-request-actions';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestDeleteConfirmationModal } from '../components/schedule-request-delete-confirmation-modal';
import { ScheduleRequestDismissConfirmationModal } from '../components/schedule-request-dismiss-confirmation-modal';
import { ScheduleRequestRejectConfirmationModal } from '../components/schedule-request-reject-confirmation-modal';
import { ScheduleRequestDetails } from '../schedule-request-item-details';
import { ScheduleRequestItemHeader } from '../schedule-request-item-header';

export type SchedulePulseRequestListItemProps = {
  listItem: ScheduleRequestListItem;
  setSelectedScheduleRequest: React.Dispatch<React.SetStateAction<ScheduleRequest | null>>;
  scheduleRequestModalProps: ReturnType<typeof useModalControl>;
};

export const SchedulePulseRequestListItem = (props: SchedulePulseRequestListItemProps) => {
  const { listItem } = props;
  const { id: requestId = '', locationId = '' } = listItem;
  const isPatientContacted = listItem.status === 'MESSAGED' || listItem.status === 'CALLED';
  const patientContactedAt = isPatientContacted ? listItem.reviewedAt : undefined;

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(locationId);
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { accordionRef } = useOpenedRequestDetails({ requestId: listItem.id, locationId: listItem.locationId });

  const deleteRequestModalProps = useModalControl();
  const dismissRequestModalProps = useModalControl();
  const rejectRequestModalProps = useModalControl();

  const {
    isLoading,
    handleMessage,
    handlePhoneCall,
    handleCompleteRequest,
    handleDeleteRequest,
    handleDismissRequest,
    setDismissActionCalledFrom,
    handleRejectRequest,
    handleReviewCallLog,
    handleViewConversation,
  } = useScheduleRequestActions(listItem);

  return (
    <>
      <Accordion.Item
        key={requestId}
        value={requestId}
        trackingId={ScheduleRequestsTrackingIds.requestListItemAccordion}
        ref={accordionRef}
      >
        <Accordion.Header
          css={[accordionHeaderOverrideStyles, debugModeV2Styles(isDebugModeOn && listItem.linkHash === 'v2')]}
        >
          <ContentLoader show={isLoading} size='small' />
          <ScheduleRequestItemHeader
            {...props}
            patientContactedAt={patientContactedAt}
            deleteRequestModalProps={deleteRequestModalProps}
            dismissRequestModalProps={dismissRequestModalProps}
            rejectRequestModalProps={rejectRequestModalProps}
            onCompleteRequest={handleCompleteRequest}
            onDismissRequestIntent={setDismissActionCalledFrom}
            onPhoneCall={handlePhoneCall}
            onMessage={handleMessage}
          />
        </Accordion.Header>
        <Accordion.Body>
          <ScheduleRequestDetails
            listItem={listItem}
            requestVersion={listItem.linkHash || ''}
            scheduleRequestId={listItem.id ?? ''}
            locationId={listItem.locationId ?? ''}
            isPaymentsEnabled={isPaymentsEnabled}
            patientContactedAt={patientContactedAt}
            dismissRequestModalProps={dismissRequestModalProps}
            onCompleteRequest={handleCompleteRequest}
            onDismissRequestIntent={setDismissActionCalledFrom}
            onPhoneCall={handlePhoneCall}
            onMessage={handleMessage}
            onReviewCallLog={handleReviewCallLog}
            onViewConversation={handleViewConversation}
          />
        </Accordion.Body>
      </Accordion.Item>

      <ScheduleRequestDeleteConfirmationModal onConfirm={handleDeleteRequest} {...deleteRequestModalProps.modalProps} />
      <ScheduleRequestRejectConfirmationModal onConfirm={handleRejectRequest} {...rejectRequestModalProps.modalProps} />
      <ScheduleRequestDismissConfirmationModal
        onConfirm={handleDismissRequest}
        {...dismissRequestModalProps.modalProps}
      />
    </>
  );
};

const accordionHeaderOverrideStyles = css`
  .header-container {
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
  }
`;

const debugModeV2Styles = (isDebugModeOn: boolean) =>
  css`
    ${isDebugModeOn ? `background-color: var(--fabric-color-red-10);` : ''}
  `;
